import { useMemo } from 'react'
import {
  classSlot,
  formatDuration,
  formatGap,
  formatLapTime,
  readClock,
  timingColumnLabels,
  sessionTrackStatus,
} from '@racedash/shared'
import type {
  CarStanding,
  ClockCheckpoint,
  ScreenConfigFor,
  TimingColumn,
} from '@racedash/shared'
import { Select } from '@mantine/core'
import type { DashboardData } from '../../dashboard/useSessionData'
import type { ScreenContext } from '../../dashboard/screenContext'
import { useLocalSetting } from '../../lib/useLocalSetting'
import { usePortalTarget } from '../../lib/usePortalTarget'
import './timing.css'

/**
 * The whole field as a table.
 *
 * The one screen that is read rather than glanced at, so unlike the widget grid
 * it scrolls — a full grid is a hundred cars and no amount of scaling fits that
 * on a screen legibly.
 */
export default function TimingScreen({
  config,
  data,
  car,
  now,
  screenId,
  context,
}: {
  config: ScreenConfigFor<'timing'>
  data: DashboardData
  car: string | null
  now: Date
  screenId: string
  context: ScreenContext
}) {
  const all = useMemo(() => {
    const list = (data.standings?.cars ?? []).slice()
    // Retired and non-starters have no position, and sorting them to the top
    // would push the leader off the first screenful.
    list.sort((a, b) => (a.position || 9999) - (b.position || 9999))
    return list
  }, [data.standings])

  const classes = useMemo(() => {
    const seen = new Set<string>()
    for (const c of all) if (c.class?.trim()) seen.add(c.class.trim())
    return [...seen].sort()
  }, [all])

  /*
   * Which class is on screen, remembered per screen in this browser.
   *
   * A personal view setting, not a property of the dashboard: two people
   * watching the same share link are usually watching different classes, and a
   * filter that travelled with the dashboard would have one of them changing
   * the other's screen.
   */
  const [klass, setKlass] = useLocalSetting<string | null>(
    `racedash.timing.${context.viewId}.${screenId}.class`,
    null,
    (raw) => (typeof raw === 'string' && raw ? raw : null),
  )

  // A class that has gone — a different session, or a filter set last week —
  // must not silently empty the table.
  const active = klass && classes.includes(klass) ? klass : null
  const cars = useMemo(
    () => (active ? all.filter((c) => c.class?.trim() === active) : all),
    [all, active],
  )

  const session = data.session
  const state = sessionTrackStatus(session)
  // Frozen once the session is over, so an abandoned session does not sit
   // there counting down. See DashboardData.asAt.
  const corrected = data.asAt(now)
  const raw = session?.clock
  const checkpoint: ClockCheckpoint | null = raw?.at
    ? {
        at: raw.at.toDate(),
        elapsedMs: raw.elapsedMs,
        remainingMs: raw.remainingMs,
        running: raw.running,
      }
    : null
  const clock = readClock(checkpoint, corrected)
  const messages = data.liveMessages?.messages ?? []
  const portal = usePortalTarget()

  return (
    <div className="timing">
      <header className="timing-head" data-tone={state.status}>
        <div className="timing-title">
          <span className="timing-session">{session?.name ?? session?.sessionNameRaw ?? '—'}</span>
          <span className="timing-series">
            {[session?.meetingNameRaw, session?.seriesNameRaw].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="timing-state">{state.label}</div>

        {classes.length > 1 && (
          <Select
            aria-label="Class"
            size="xs"
            w={150}
            allowDeselect={false}
            className="timing-class-filter"
            data={[
              { value: '', label: `All classes (${all.length})` },
              ...classes.map((c) => ({ value: c, label: c })),
            ]}
            value={active ?? ''}
            onChange={(v) => setKlass(v || null)}
            comboboxProps={{ portalProps: { target: portal } }}
          />
        )}
        {clock && (
          <div className="timing-clock">
            {formatDuration(clock.remainingMs > 0 ? clock.remainingMs : clock.elapsedMs)}
            {/* Said rather than implied: a countdown that has stopped moving
                looks exactly like one the page has stopped updating. */}
            {!clock.running && <span className="timing-clock-note">stopped</span>}
          </div>
        )}
      </header>

      {/* What race control is showing now, not the log — nothing records when a
          message left the board, so age alone cannot tell them apart. */}
      {messages.length > 0 && (
        <div className="timing-rc">
          {messages.map((m, i) => (
            // No timestamp. This is what is on the board *now*; when it first
            // appeared is a question the message log answers, and putting it
            // here invited the whole strip to be read as a log.
            <div className="timing-rc-row" key={`${m.id}-${m.at}-${i}`}>
              <span className="timing-rc-what">{m.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="timing-scroll">
        <table className="timing-table">
          <thead>
            <tr>
              {config.columns.map((c) => (
                <th key={c} className={`col-${c}`}>
                  {timingColumnLabels[c]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cars.map((entry) => (
              <tr
                key={entry.number}
                // The row you look for first, and the reason a timing table
                // takes a car parameter at all.
                data-mine={car && entry.number === car ? 'true' : undefined}
                data-state={entry.state?.toLowerCase()}
              >
                {config.columns.map((c) => (
                  <td key={c} className={`col-${c}`}>
                    {cell(entry, c, data)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {cars.length === 0 && <p className="timing-empty">No cars on track yet.</p>}
      </div>
    </div>
  )
}

function cell(entry: CarStanding, column: TimingColumn, data: DashboardData): React.ReactNode {
  switch (column) {
    case 'position':
      return entry.position > 0 ? entry.position : '—'
    case 'number':
      return entry.number
    case 'state':
      return <StateChip state={entry.state} />
    case 'class':
      return <ClassChip name={entry.class} />
    case 'classPosition':
      return entry.classPosition && entry.classPosition > 0 ? entry.classPosition : '—'
    case 'driver':
      return driverCell(entry, data)
    case 'team':
      return entry.team ?? '—'
    case 'laps':
      return entry.lap && entry.lap > 0 ? entry.lap : '—'
    case 'gap':
      return formatGap(entry.gapToLeaderMs, entry.gapToLeaderLaps ?? 0)
    case 'interval':
      return formatGap(entry.intervalAheadMs, entry.intervalAheadLaps ?? 0)
    case 's1':
      return formatLapTime(entry.s1Ms)
    case 's2':
      return formatLapTime(entry.s2Ms)
    case 's3':
      return formatLapTime(entry.s3Ms)
    case 'last':
      return formatLapTime(entry.lastLapMs)
    case 'best':
      return formatLapTime(entry.bestLapMs)
    case 'pits':
      return entry.stops ?? '—'
  }
}

/**
 * Who is driving, and on whose authority.
 *
 * The first real consumer of the declaration overlay. Where the organisation has
 * said who is in the car its answer wins, marked so it is not mistaken for the
 * timekeeper's — which matters because the two disagree routinely: the vendor is
 * told about a change late, or by hand, and sometimes not at all.
 */
function driverCell(entry: CarStanding, data: DashboardData): React.ReactNode {
  const declared = data.declaredDrivers?.get(entry.number.trim())
  if (declared) {
    return (
      <span className="timing-driver" title="Declared by your organisation">
        {declared}
        <span className="timing-driver-mark">•</span>
      </span>
    )
  }
  return entry.driver || '—'
}

/** RUN / IN / OUT / FUEL, already canonical by the time it reaches here. */
/**
 * A class, in the colour that class always has.
 *
 * The slot is a pure function of the name, so it survives a refresh, a change
 * of leader, and a car being added or removed. See classSlot — the alternatives
 * all recolour the field when something unrelated moves, which is worse than
 * no colour at all.
 */
function ClassChip({ name }: { name: string | undefined }) {
  if (!name?.trim()) return <span className="timing-dim">—</span>
  return (
    <span className="class-chip" data-slot={classSlot(name)}>
      {name}
    </span>
  )
}

/**
 * Where the car is, as a colour first and a word second.
 *
 * The one question this column exists to answer is "is it circulating", and on
 * a sixty-row table that has to be answerable by scanning rather than reading.
 * Deliberately a different palette from the track state above: that is the
 * condition of the race and this is the condition of one car, and they must not
 * be confusable at a glance.
 */
function StateChip({ state }: { state: string | undefined }) {
  const label = stateLabel(state)
  if (label === '—') return <span className="timing-dim">—</span>
  return (
    <span className="state-chip" data-state={(state ?? '').toUpperCase()}>
      {label}
    </span>
  )
}

function stateLabel(state: string | undefined): string {
  switch ((state ?? '').toUpperCase()) {
    case 'RUN':
      return 'Run'
    case 'IN':
      return 'In'
    case 'OUT':
      return 'Out'
    case 'FUEL':
      return 'Fuel'
    default:
      return '—'
  }
}
